import { Component } from '@poa/core';
import { observable, observer, Observer } from '@poa/state';
import React from 'react';
import { Tooltip } from 'react-tippy';
import 'react-tippy/dist/tippy.css';
import { ACCESS_LEVEL } from '../../store/auth/auth.constants';
import { getSocialIcon } from '../icons/social';
import { findIsFilterOnServerSideHeaderId, getValue } from './data-table-helpers';
import ExternalLinkContextMenu from './external-link';

const placeholederImage = require('./placeholder.jpg');

const DataTableCell = observer(
  class extends React.Component {
    static defaultProps = {
      isLink: false,
      style: {}
    };

    onDblClick = () => {
      const { id, label, data } = this.props;
      this.props.onDblClick(id, label, data[id].value);
    };

    getIsDecimal() {
      // ignore this columns on decimal check
      if (this.props.id === 'panel' || this.props.id === 'number' || this.props.id === 'nick') {
        return false;
      }

      return this.props.isDecimalThunk();
    }

    render() {
      const { id, data, className, isLink } = this.props;
      const currentField = data[id];

      let content;

      if (currentField && currentField.type) {
        switch (currentField.type) {
          case 'link':
            content = <a href={currentField.value}>{currentField.label}</a>;
            break;
          default:
            content = getValue(currentField.value, this.getIsDecimal());
            break;
        }
      } else {
        content = getValue(currentField.value, this.getIsDecimal());
      }

      if (isLink) {
        content = (
          <ExternalLinkContextMenu
            styles={{}}
            label={currentField.value}
            url={currentField.value}
          />
        );
      }

      if (this.props.valueMapper) {
        content = this.props.valueMapper({ headerId: this.props.id, data: currentField });
      }

      return (
        <td
          onDoubleClick={this.onDblClick}
          className={`table__${id} ${className}`}
          style={{ width: this.props.width, ...this.props.style }}
        >
          {content}
        </td>
      );
    }
  }
);

const DataTableButtonCell = observer(
  class extends React.Component {
    static defaultProps = {
      isLink: false,
      style: {}
    };

    render() {
      const { id, data, className } = this.props;
      const currentField = data[id];

      return (
        <td
          className={`table__${id} ${className}`}
          style={{ width: this.props.width, ...this.props.style }}
        >
          <Observer>
            {() => (
              <button onClick={() => { this.props.onClickModalOpen(currentField.value) }} style={{ ...this.props.style }}>
                {currentField.value}
              </button>
            )}
          </Observer>
        </td>
      );
    }
  }
);

class DataTableBody extends React.Component {
  componentDidMount() {
    document.addEventListener('scroll', this.hideTooltip);
  }

  componentWillUnmount() {
    document.removeEventListener('scroll', this.hideTooltip);
  }

  handleButtonPress = id => () => {
    this.buttonPressTimer = setTimeout(() => {
      if (id === this.localState.tooltipOpened) {
        this.localState.tooltipOpened = '';
        return;
      }

      this.localState.tooltipOpened = id;
    }, 1500);
  };

  handleButtonRelease = () => {
    clearTimeout(this.buttonPressTimer);
  };

  disableContextMenu = rowUniqueKey => event => {
    event.preventDefault();

    document.addEventListener('click', this.handleOutsideClick);

    this.localState.tooltipOpened = rowUniqueKey;
  };

  handleOutsideClick = e => {
    if (this.popup && this.popup.contains(e.target)) {
      return;
    }

    this.hideTooltip();
  };

  localState = observable({ tooltipOpened: '' });

  hideTooltip = () => {
    this.localState.tooltipOpened = '';
    document.removeEventListener('click', this.handleOutsideClick);
  };

  render() {
    const { headersOrder, data, onFilter: onFilterClick, requestModalOpen } = this.props;

    const tbody = [];

    data.get().tbody.forEach((row, idx) => {
      const status = row.status;
      const id = row.id;
      const rowWithoutStatus = { ...row };
      const body = [];

      delete rowWithoutStatus.status;
      delete rowWithoutStatus.id;

      headersOrder.get().forEach((columnSetting, index) => {
        if (columnSetting.name.includes('@@')) {
          return;
        }

        const row = rowWithoutStatus[columnSetting.sectionId];
        const headerId = columnSetting.name;

        const nextColumnSetting = headersOrder.get()[index - 1];
        const shouldShowSeparator = (nextColumnSetting || { name: '' }).name.startsWith('@@');

        if (columnSetting.isSelected === false) {
          return;
        }

        if (!row[headerId]) {
          switch (headerId) {

            case 'more':
              const rowUniqueKey = `row-${headerId}-${row['id'].value}-more`;

              body.push(
                <td
                  key={rowUniqueKey}
                  id={rowUniqueKey}
                  style={{ width: columnSetting.width }}
                  className={`table__${headerId} ${shouldShowSeparator ? 'table__separator' : ''}`}
                >
                  <Tooltip
                    interactive={true}
                    arrow={true}
                    trigger='click focus'
                    theme='light'
                    key={2}
                    html={
                      <div style={{ padding: '15px' }}>
                        {Object.keys(row).map(key => (
                          <div style={{ textAlign: 'left' }}>
                            <span style={{ fontWeight: 'bold' }}>{key}</span>:{' '}
                            <span style={{ whiteSpace: 'pre-wrap' }}>{row[key].value}</span>
                          </div>
                        ))}
                      </div>
                    }
                    position='bottom'
                  >
                    <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>more</span>
                  </Tooltip>
                </td>
              );
              break;
            default:
              body.push(
                <td
                  key={`row-${id}-${headerId}`}
                  className={`table__${headerId} ${shouldShowSeparator ? 'table__separator' : ''}`}
                />
              );
              break;
          }

          return;
        }

        const onFilter = (...args) =>
          onFilterClick(...args, findIsFilterOnServerSideHeaderId(data.get().thead, headerId));

        const rowUniqueKey = `row-${id}-${headerId}-${row[headerId].value}`;

        switch (headerId) {
          case 'from':
          case 'to':
          case 'sender':
          case 'recipient':
            body.push(
              <DataTableCell
                className={`${shouldShowSeparator ? 'table__separator' : ''}`}
                key={rowUniqueKey}
                isDecimalThunk={this.props.isDecimalThunk}
                id={headerId}
                isLink
                label={columnSetting.label}
                data={row}
                valueMapper={this.props.valueMapper}
                width={columnSetting.width}
                onDblClick={onFilter}
              />
            );
            break;
          case 'status':
            body.push(
              <td
                key={rowUniqueKey}
                style={{ width: columnSetting.width }}
                className={`table__${headerId} ${shouldShowSeparator ? 'table__separator' : ''}`}
              >
                <span className='point' />
              </td>
            );
            break;
          case 'number':
            body.push(
              <DataTableCell
                className={`${shouldShowSeparator ? 'table__separator' : ''}`}
                key={rowUniqueKey}
                isDecimalThunk={this.props.isDecimalThunk}
                id={headerId}
                data={{
                  [headerId]: { value: row[headerId].value ? `№${row[headerId].value}` : '-' }
                }}
                onDblClick={() => {
                  onFilter(headerId, columnSetting.label, row[headerId].value);
                }}
                width={columnSetting.width}
              />
            );
            break;
          case 'operator':
            body.push(
              <DataTableCell
              className={`${shouldShowSeparator ? 'table__separator' : ''}`}
              key={rowUniqueKey}
              isDecimalThunk={this.props.isDecimalThunk}
              id={headerId}
              label={columnSetting.label}
              data={row}
              width={columnSetting.width}
              onDblClick={onFilter}
              valueMapper={this.props.valueMapper}
              style={row['operator_is_new'] && row['operator_is_new'].value ? { color: 'green', fontWeight: 'bold' } : {}}
              />
            );
            break;
          case 'is_active': {
            const value = row[headerId].value;

            body.push(
              <DataTableCell
                className={`${shouldShowSeparator ? 'table__separator' : ''}`}
                key={rowUniqueKey}
                isDecimalThunk={this.props.isDecimalThunk}
                id={headerId}
                style={{ width: columnSetting.width, color: value ? 'green' : 'black' }}
                data={{
                  [headerId]: { value: row[headerId].value ? `активен` : 'неактивен' }
                }}
                onDblClick={() => {
                  onFilter(
                    headerId,
                    row[headerId].value ? 'Активен' : 'Неактивен',
                    row[headerId].value
                  );
                }}
                width={columnSetting.width}
              />
            );
            break;
          }
          case 'client':
            body.push(
              <DataTableButtonCell
                className={`${shouldShowSeparator ? 'table__separator' : ''}`}
                key={rowUniqueKey}
                isDecimalThunk={this.props.isDecimalThunk}
                id={headerId}
                label={columnSetting.label}
                data={row}
                valueMapper={this.props.valueMapper}
                width={columnSetting.width}
                style={{ fontWeight: 'bold', background: 'transparent' }}
                onClickModalOpen={requestModalOpen}
              />
            );
            break;
          case 'photo':
            const value = row[headerId].value || placeholederImage;

            let image_button = <img
              src={value}
              onError={(e) => { e.target.src = placeholederImage; }}
              alt=''
              onClick={() => {
                requestModalOpen(id);
              }} />;


            switch (this.store.auth.accessLevel) {
              case ACCESS_LEVEL.operator:
                image_button = <img src={value} alt='' />;
                break;
              default:
                break;
            }

            body.push(
              <td
                key={rowUniqueKey}
                id={rowUniqueKey}
                onTouchStart={this.handleButtonPress(rowUniqueKey)}
                onTouchEnd={this.handleButtonRelease}
                onMouseDown={this.handleButtonPress(rowUniqueKey)}
                onMouseUp={this.handleButtonRelease}
                onMouseLeave={this.handleButtonRelease}
                style={{ width: columnSetting.width }}
                className={`table__${headerId} ${shouldShowSeparator ? 'table__separator' : ''}`}
                onContextMenu={this.disableContextMenu(rowUniqueKey)}
              >
                <Observer>
                  {() => (
                    <div className='picture'>
                      {image_button}
                    </div>
                  )}
                </Observer>
              </td>
            );
            break;
          case 'shifts':

            const shiftValues = row[headerId].value;

            body.push(
              <td
                key={rowUniqueKey}
                style={{ width: columnSetting.width }}
                className={`table__${headerId} ${shouldShowSeparator ? 'table__separator' : ''}`}
              > 
                
                <ul className='table_shifts_list'>
                  {shiftValues.map((shift, index) => {
                    return (
                      <Tooltip
                      interactive={false}
                      arrow={true}
                      trigger='mouseenter'
                      theme='light'
                      key={index}
                      html={
                        <div style={{ padding: '15px' }}>
                          <span style={{ fontWeight: 'bold' }}>{shift.operator}</span>
                          <br/>
                          <br/>
                          <span style={{ whiteSpace: 'pre-wrap' }}>{shift.from}:00 - {shift.to}:00</span>
                        </div>
                      }
                      position='top'
                    >
                        <li style={{ backgroundColor: `${shift.color}`}}></li>
                    </Tooltip>

                    )
                  }
                )}

                </ul>
              </td>
            );
            break;

          case 'social':
            body.push(
              <td
                key={rowUniqueKey}
                style={{ width: columnSetting.width }}
                className={`table__${headerId} ${shouldShowSeparator ? 'table__separator' : ''}`}
              >
                {row[headerId].value.map(social => {
                  return (
                    <a
                      key={social.url}
                      className='social-link'
                      href={social.url}
                      title={social.type}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      {getSocialIcon(social.type)}
                    </a>
                  );
                })}
              </td>
            );
            break;
          default:
            body.push(
              <DataTableCell
                className={`${shouldShowSeparator ? 'table__separator' : ''}`}
                key={rowUniqueKey}
                isDecimalThunk={this.props.isDecimalThunk}
                id={headerId}
                label={columnSetting.label}
                data={row}
                width={columnSetting.width}
                onDblClick={onFilter}
                valueMapper={this.props.valueMapper}
              />
            );
            break;
        }
      });

      tbody.push(
        <tr key={idx} className={`${status} table-row`}>
          {body}
        </tr>
      );
    });

    return <tbody>{tbody}</tbody>;
  }
}

export default Component({})(DataTableBody);
